import { HttpClient } from "@angular/common/http";
import { ChangeDetectorRef, Injectable } from "@angular/core";
import {
  AccessFlow,
  AccessFlowCheckResponse,
  AdaptiveAccessControlNode,
  PrincipalType,
} from "@aptsi-types";
import { environment } from "@env";
import { BehaviorSubject, Observable, Subject } from "rxjs";
import { catchError, filter, map, takeUntil } from "rxjs/operators";
import { ErrorHandlingService } from "../shared/errorHandling/errorHandling.service";
import { API_ENDPOINTS } from "../ada-access-api-endpoints";
import { NavigationEnd, Router } from "@angular/router";

interface GetCheckAccessFlow {
  check: AccessFlowCheckResponse
}

@Injectable({
  providedIn: "root",
})
export class AccessFlowService {
  private readonly accessFlowUrl = environment.apiUrl;
  existingAccessFlowId: string = null;

  private finalEntries: AdaptiveAccessControlNode[] = [];
  private destroy$ = new Subject<void>(); // For unsubscribing

  constructor(
    private http: HttpClient,
    private errorHandlingService: ErrorHandlingService,
    private router: Router
  ) { }

  private selectedEntriesSubject = new BehaviorSubject<
    AdaptiveAccessControlNode[]
  >([]);
  selectedEntries$ = this.selectedEntriesSubject.asObservable();

  public finalEntriesSubject = new BehaviorSubject<AdaptiveAccessControlNode[]>(
    []
  );
  finalEntries$ = this.finalEntriesSubject.asObservable();

  setFinalEntries(entries: AdaptiveAccessControlNode[]) {
    this.finalEntries = [...entries]; // Ensure local copy is updated
    this.finalEntriesSubject.next(this.finalEntries);
  }

  // Get current entries
  getFinalEntries(): AdaptiveAccessControlNode[] {
    return [...this.finalEntries]; // Return a fresh copy to avoid mutation issues
  }

  // Add new unique entries
  addEntries(newEntries: AdaptiveAccessControlNode[]) {
    const currentEntries = this.getFinalEntries();

    // Merge existing and new entries, removing duplicates
    const updatedEntries = [
      ...currentEntries.filter(
        (existing) =>
          !newEntries.some(
            (newEntry) =>
              newEntry.adaptiveaccessnodeid === existing.adaptiveaccessnodeid
          )
      ),
      ...newEntries,
    ];

    this.setFinalEntries(updatedEntries);
  }

  // Remove selected entries
  removeEntries(entriesToRemove: AdaptiveAccessControlNode[]) {
    const updatedEntries = this.getFinalEntries().filter(
      (entry) =>
        !entriesToRemove.some(
          (removeEntry) =>
            removeEntry.adaptiveaccessnodeid === entry.adaptiveaccessnodeid
        )
    );

    this.setFinalEntries(updatedEntries);
  }

  getAccessFlows(currentPage: number): Observable<AccessFlow[]> {
    return this.http
      .post<{ accessflows: AccessFlow[] }>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.FETCH}`,
        { currentPage }
      )
      .pipe(
        map((response) => response.accessflows),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getAccessFlowSearch(
    currentPage: number,
    searchText: String,
    filterByHeader: String
  ) {
    return this.http
      .post<{ accessflows: AccessFlow[] }>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.SEARCH}`,
        { currentPage, searchText, filterByHeader }
      )
      .pipe(
        map((response) => response.accessflows),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getAccessFlow(accessflowid: string): Observable<AccessFlow> {
    return this.http
      .post<{ accessflow: AccessFlow }>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.GET}`,
        { accessflowid }
      )
      .pipe(
        map((response) => response.accessflow),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

getAdaptiveAccessNodes(accessFlowId: string): Observable<AdaptiveAccessControlNode[]> {
  return this.http
    .post<{ accessflows: any[] }>(
      `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.GETNODES}`,
      { accessFlowId }
    )
    .pipe(
      map((response) =>
        (response.accessflows || []).map(node => ({
          adaptiveaccessnodeid: node.adaptiveaccessnodeid,
          adaptiveAccessNodeName: node.adaptiveAccessNodeName,
          adaptiveAccessNodeType: node.adaptiveAccessNodeType || node.traversalLogic,

          // required because your interface needs them
          assetId: node.assetId || null,
          authenticationprotocol: node.authenticationprotocol || null,
          authenticationendpoint: node.authenticationendpoint || null,
          rulesetId: node.rulesetId || null
        }))
      ),
      catchError((error) => this.errorHandlingService.handleError(error))
    );
}


  addNodeConfig(
    accessFlowId: string,
    nodeIds: string[]
  ): Observable<AccessFlow> {
    return this.http
      .post<AccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.ADD}`,
        {
          accessFlowId,
          nodeIds,
        }
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  updateNodeConfig(
    accessFlowId: string,
    nodeIds: string[]
  ): Observable<AccessFlow> {
    return this.http
      .post<AccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.UPDATENODES}`,
        {
          accessFlowId,
          nodeIds,
        }
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  createAccessFlow(
    name: string,
    assetid: number,
    principaltypeid: string,
    isActive: boolean,
    organizationId: string,
  ): Observable<AccessFlow> {
    return this.http
      .post<AccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.CREATE}`,
        {
          name,
          assetid,
          principaltypeid,
           isActive,
          organizationId    
        }
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  updateAccessFlow(
    accessflowid: number,
    name: string,
    assetid: number,
    principaltypeid: string,
    isActive: boolean,
    organizationId: string,
    
  ): Observable<AccessFlow> {
    return this.http
      .post<AccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.UPDATE}`,
        {
          accessflowid,
          name,
          assetid,
          principaltypeid,
          isActive,
          organizationId,        
        }
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  saveFlow(): Observable<AccessFlow> {
    return this.http
      .post<AccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.FINISH}`,
        {}
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  deleteAccessFlow(accessflowid: string): Observable<AccessFlow[]> {
    return this.http
      .post<AccessFlow[]>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.DELETE}`,
        { accessflowid }
      )
      .pipe(
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  getsPrincipalType(): Observable<PrincipalType[]> {
    return this.http
      .post<{ principalTypes: PrincipalType[] }>(
        `${this.accessFlowUrl}${API_ENDPOINTS.PRINCIPALTYPE.FETCH}`,
        {}
      )
      .pipe(
        map((response) => response.principalTypes),
        catchError((error) => this.errorHandlingService.handleError(error))
      );
  }

  generateAccessFlow(organizationId: string): Observable<{ message: string }> {
    console.log('[Service] generateAccessFlow called with orgId:', organizationId);

    return this.http
      .post<{ accessflow: { message: string } }>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.GENERATE}`,
        { organizationId }
      )
      .pipe(
        map((response) => {
          console.log('[Service] generateAccessFlow raw response:', response);
          if (!response?.accessflow?.message) {
            throw new Error('Malformed response: Missing message key');
          }
          return response.accessflow;
        }),
        catchError((error) => {
          console.error('[Service] generateAccessFlow error:', error);
          return this.errorHandlingService.handleError(error);
        })
      );
  }

  checkAccessFlow(organizationId: string){
    console.log('[Service] checkAccessFlow called with orgId:', organizationId);

    return this.http
      .post<GetCheckAccessFlow>(
        `${this.accessFlowUrl}${API_ENDPOINTS.ACCESS_FLOW.CHECK}`,
        { organizationId }
      )
      .pipe(
        map((response) => response.check),
        catchError((error) => {
          console.error('[Service] checkAccessFlow error:', error);
          return this.errorHandlingService.handleError(error);
        })
      );
  }




}
